/**
 * SidebarConfigEditor.jsx - Editor per voci di menu sidebar dashboard
 * Gestisce blueprint_sidebar_items via CMS
 */

import React, { useState, useEffect } from 'react';
import supabase from '../../lib/supabase';
import { 
  Plus, Save, Trash2, Edit, Check, X, Loader2, 
  GripVertical, ChevronUp, ChevronDown, Eye, EyeOff,
  Home, FolderKanban, Layers, Search, Sparkles, Bookmark,
  Users, Settings, LayoutDashboard, Zap, Database, Star
} from 'lucide-react';

// Available icons for sidebar items
const ICON_OPTIONS = [
  { value: 'Home', label: 'Home', Icon: Home },
  { value: 'FolderKanban', label: 'Progetti', Icon: FolderKanban },
  { value: 'Layers', label: 'Layers', Icon: Layers },
  { value: 'Search', label: 'Ricerca', Icon: Search },
  { value: 'Sparkles', label: 'Sparkles', Icon: Sparkles },
  { value: 'Bookmark', label: 'Salvati', Icon: Bookmark },
  { value: 'Users', label: 'Utenti', Icon: Users },
  { value: 'Settings', label: 'Settings', Icon: Settings },
  { value: 'LayoutDashboard', label: 'Dashboard', Icon: LayoutDashboard },
  { value: 'Zap', label: 'Zap', Icon: Zap },
  { value: 'Database', label: 'Database', Icon: Database },
  { value: 'Star', label: 'Star', Icon: Star }
];

const getIconComponent = (iconName) => {
  const found = ICON_OPTIONS.find(i => i.value === iconName);
  return found?.Icon || Layers;
};

const SidebarConfigEditor = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({
    slug: '',
    label_it: '',
    label_en: '',
    path: '',
    icon: 'Layers',
    is_active: true,
    requires_auth: true,
    requires_admin: false,
    visible_to_roles: 'all',
    order_index: 0,
    show_badge: false,
    badge_type: null
  });

  // Load sidebar items
  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('blueprint_sidebar_items')
        .select('*')
        .order('order_index', { ascending: true });
      
      if (error) throw error;
      setItems(data || []);
    } catch (err) {
      console.error('Error loading sidebar items:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveItem = async (item) => {
    setSaving(true);
    try {
      if (item.id) {
        // Update existing
        const { error } = await supabase
          .from('blueprint_sidebar_items')
          .update({
            slug: item.slug,
            label_it: item.label_it,
            label_en: item.label_en,
            path: item.path,
            icon: item.icon,
            is_active: item.is_active,
            requires_auth: item.requires_auth,
            requires_admin: item.requires_admin,
            visible_to_roles: item.visible_to_roles,
            order_index: item.order_index,
            show_badge: item.show_badge,
            badge_type: item.badge_type,
            updated_at: new Date().toISOString()
          })
          .eq('id', item.id);
        
        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from('blueprint_sidebar_items')
          .insert([{
            slug: item.slug,
            label_it: item.label_it,
            label_en: item.label_en,
            path: item.path,
            icon: item.icon,
            is_active: item.is_active,
            requires_auth: item.requires_auth,
            requires_admin: item.requires_admin,
            visible_to_roles: item.visible_to_roles,
            order_index: items.length,
            show_badge: item.show_badge,
            badge_type: item.badge_type
          }]);
        
        if (error) throw error;
      }
      
      await loadItems();
      setEditingItem(null);
      setShowAddForm(false);
      setNewItem({
        slug: '',
        label_it: '',
        label_en: '',
        path: '',
        icon: 'Layers',
        is_active: true,
        requires_auth: true,
        requires_admin: false,
        visible_to_roles: 'all',
        order_index: 0,
        show_badge: false,
        badge_type: null
      });
    } catch (err) {
      console.error('Error saving item:', err);
      alert('Errore: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Eliminare questa voce?')) return;
    
    try {
      const { error } = await supabase
        .from('blueprint_sidebar_items')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      await loadItems();
    } catch (err) {
      console.error('Error deleting item:', err);
      alert('Errore: ' + err.message);
    }
  };

  const moveItem = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= items.length) return;
    
    const newItems = [...items];
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    
    // Update order_index for all items
    try {
      for (let i = 0; i < newItems.length; i++) {
        await supabase
          .from('blueprint_sidebar_items')
          .update({ order_index: i })
          .eq('id', newItems[i].id);
      }
      setItems(newItems);
    } catch (err) {
      console.error('Error reordering:', err);
    }
  };

  const toggleActive = async (item) => {
    try {
      const { error } = await supabase
        .from('blueprint_sidebar_items')
        .update({ is_active: !item.is_active })
        .eq('id', item.id);
      
      if (error) throw error;
      await loadItems();
    } catch (err) {
      console.error('Error toggling active:', err);
    }
  };

  const ItemForm = ({ item, onSave, onCancel }) => {
    const [formData, setFormData] = useState(item);
    
    return (
      <div className="bg-[#1a1a18] rounded-lg p-4 border border-white/10 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-white/50 mb-1">Slug (ID univoco)</label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              className="w-full bg-[#0a0a0a] border border-white/10 rounded px-3 py-2 text-sm text-white"
              placeholder="es. dashboard"
            />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1">Path (URL)</label>
            <input
              type="text"
              value={formData.path}
              onChange={(e) => setFormData({ ...formData, path: e.target.value })}
              className="w-full bg-[#0a0a0a] border border-white/10 rounded px-3 py-2 text-sm text-white"
              placeholder="es. /dashboard"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-white/50 mb-1">Label IT</label>
            <input
              type="text"
              value={formData.label_it}
              onChange={(e) => setFormData({ ...formData, label_it: e.target.value })}
              className="w-full bg-[#0a0a0a] border border-white/10 rounded px-3 py-2 text-sm text-white"
              placeholder="Dashboard"
            />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1">Label EN</label>
            <input
              type="text"
              value={formData.label_en || ''}
              onChange={(e) => setFormData({ ...formData, label_en: e.target.value })}
              className="w-full bg-[#0a0a0a] border border-white/10 rounded px-3 py-2 text-sm text-white"
              placeholder="Dashboard"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-white/50 mb-1">Icona</label>
            <select
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              className="w-full bg-[#0a0a0a] border border-white/10 rounded px-3 py-2 text-sm text-white"
            >
              {ICON_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1">Ruoli visibili</label>
            <input
              type="text"
              value={formData.visible_to_roles}
              onChange={(e) => setFormData({ ...formData, visible_to_roles: e.target.value })}
              className="w-full bg-[#0a0a0a] border border-white/10 rounded px-3 py-2 text-sm text-white"
              placeholder="all o architect,designer"
            />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1">Badge</label>
            <select
              value={formData.badge_type || ''}
              onChange={(e) => setFormData({ 
                ...formData, 
                badge_type: e.target.value || null,
                show_badge: !!e.target.value
              })}
              className="w-full bg-[#0a0a0a] border border-white/10 rounded px-3 py-2 text-sm text-white"
            >
              <option value="">Nessuno</option>
              <option value="new">NEW</option>
              <option value="pro">PRO</option>
              <option value="count">Count</option>
            </select>
          </div>
        </div>
        
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 rounded"
            />
            <span className="text-sm text-white/70">Attivo</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.requires_auth}
              onChange={(e) => setFormData({ ...formData, requires_auth: e.target.checked })}
              className="w-4 h-4 rounded"
            />
            <span className="text-sm text-white/70">Richiede login</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.requires_admin}
              onChange={(e) => setFormData({ ...formData, requires_admin: e.target.checked })}
              className="w-4 h-4 rounded"
            />
            <span className="text-sm text-white/70">Solo Admin</span>
          </label>
        </div>
        
        <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-white/60 hover:text-white"
          >
            Annulla
          </button>
          <button
            onClick={() => onSave(formData)}
            disabled={saving || !formData.slug || !formData.label_it || !formData.path}
            className="px-4 py-2 text-sm bg-[#B08B5C] text-black rounded font-medium hover:bg-[#C9A46E] disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salva
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#B08B5C]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-medium text-white">Sidebar Menu</h2>
          <p className="text-white/50 text-sm mt-1">Gestisci le voci del menu laterale della dashboard</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#B08B5C] text-black rounded-lg text-sm font-medium hover:bg-[#C9A46E]"
        >
          <Plus className="w-4 h-4" />
          Aggiungi voce
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <ItemForm
          item={newItem}
          onSave={saveItem}
          onCancel={() => setShowAddForm(false)}
        />
      )}

      {/* Items List */}
      <div className="space-y-2">
        {items.map((item, index) => {
          const IconComponent = getIconComponent(item.icon);
          
          if (editingItem?.id === item.id) {
            return (
              <ItemForm
                key={item.id}
                item={editingItem}
                onSave={saveItem}
                onCancel={() => setEditingItem(null)}
              />
            );
          }
          
          return (
            <div
              key={item.id}
              className={`flex items-center gap-4 p-4 rounded-lg border transition-colors ${
                item.is_active 
                  ? 'bg-[#1a1a18] border-white/10' 
                  : 'bg-[#0a0a0a] border-white/5 opacity-50'
              }`}
            >
              {/* Drag Handle & Order */}
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => moveItem(index, -1)}
                  disabled={index === 0}
                  className="p-1 text-white/30 hover:text-white disabled:opacity-20"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  onClick={() => moveItem(index, 1)}
                  disabled={index === items.length - 1}
                  className="p-1 text-white/30 hover:text-white disabled:opacity-20"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
              
              {/* Icon */}
              <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
                <IconComponent className="w-5 h-5 text-[#B08B5C]" />
              </div>
              
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-white">{item.label_it}</span>
                  {item.show_badge && item.badge_type && (
                    <span className={`px-1.5 py-0.5 text-[10px] rounded-full font-medium ${
                      item.badge_type === 'new' ? 'bg-[#B08B5C] text-black' :
                      item.badge_type === 'pro' ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-black' :
                      'bg-white/20 text-white'
                    }`}>
                      {item.badge_type.toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-white/40">
                  <span>{item.path}</span>
                  <span>•</span>
                  <span>{item.slug}</span>
                  {item.requires_admin && (
                    <>
                      <span>•</span>
                      <span className="text-red-400">Admin</span>
                    </>
                  )}
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleActive(item)}
                  className={`p-2 rounded-lg transition-colors ${
                    item.is_active ? 'text-green-400 hover:bg-green-500/10' : 'text-white/30 hover:bg-white/5'
                  }`}
                  title={item.is_active ? 'Disattiva' : 'Attiva'}
                >
                  {item.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => setEditingItem(item)}
                  className="p-2 text-white/50 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => deleteItem(item.id)}
                  className="p-2 text-red-400/50 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {items.length === 0 && !showAddForm && (
        <div className="text-center py-12 text-white/40">
          <p>Nessuna voce di menu configurata</p>
          <button
            onClick={() => setShowAddForm(true)}
            className="mt-2 text-[#B08B5C] hover:underline"
          >
            Aggiungi la prima voce
          </button>
        </div>
      )}
    </div>
  );
};

export default SidebarConfigEditor;
